/**
 * NotFoundPage — Phase O.5 (purchase-web migration).
 *
 * Displays a 404 error page for unknown marketing site routes.
 * Provides navigation links back to the homepage and contact page.
 *
 * @module portals/marketing-website/pages/NotFoundPage
 */

import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getBrand } from '@puffin-core/brand-assets';
import { MarketingSeo, useAnalytics, webPage, breadcrumbList } from '@puffin-core/marketing-shell';

import { PURCHASE_MARKETING_ROUTES } from '../constants/purchaseMarketingRoutePaths';

const APP_KEY = 'purchase';
// 🔴 `PAGE_TITLE` and `PAGE_DESCRIPTION` used to live here as English string literals, evaluated
// ONCE at import — before any user has a locale — so no `changeLanguage` could move them. They fed
// both the <MarketingSeo> head AND the JSON-LD, so a Spanish visitor got an English meta
// description in search results and English structured data. Invisible to
// `web/no-hardcoded-strings`, which sees JSX attributes, not module-scope constants. Now resolved
// at render from `notFound.seoTitle` / `notFound.seoDescription`.
const PAGE_CANONICAL = 'https://purchasepuffin.com/404';

/**
 * 404 page for unknown marketing-website routes. Renders the canonical
 * <MarketingSeo> head + JSON-LD plus three quick navigation links.
 *
 * @returns {React.JSX.Element} The 404 page.
 */
function NotFoundPage() {
  // `getBrand` is a plain registry read (not a hook), so `brand` — and therefore
  // the jsonLd memo built from it — carries no hook dependency. The memo is
  // consequently placed FIRST, satisfying the canonical
  // state → effect → computed → custom ordering. It is evaluated at render time
  // (not module load), so test suites that stub `@puffin-core/marketing-shell`
  // still resolve `webPage` / `breadcrumbList` normally.
  const { t } = useTranslation();
  const { trackPageView } = useAnalytics();

  useEffect(() => {
    trackPageView('/404', 'Page Not Found');
  }, [trackPageView]);

  const brand = getBrand(APP_KEY);

  // Single SEO derivation point — title, description and JSON-LD recompute together when the
  // translator's identity changes (which tracks the active language), so the structured data can
  // never disagree with the rendered head. Mirrors payday-web's NotFoundPage, whose hook order
  // (custom hooks, then effect, then computed) lints clean under `web/component-state-order`.
  const seo = useMemo(() => {
    const title = t('notFound.seoTitle');
    const description = t('notFound.seoDescription');
    return {
      title,
      description,
      jsonLd: [
        webPage({
          appKey: APP_KEY,
          brand,
          url: PAGE_CANONICAL,
          title,
          description,
        }),
        breadcrumbList({
          crumbs: [
            { name: 'Home', url: 'https://purchasepuffin.com/' },
            { name: 'Not Found', url: PAGE_CANONICAL },
          ],
        }),
      ],
    };
  }, [brand, t]);

  const ogImageUrl = `https://purchasepuffin.com${brand.assets.ogCard}`;

  return (
    <div className="not-found-marketing">
      <MarketingSeo
        title={seo.title}
        description={seo.description}
        canonical={PAGE_CANONICAL}
        ogType="website"
        ogImage={ogImageUrl}
        jsonLd={seo.jsonLd}
      />
      <p className="not-found-code">{t('notFound.code')}</p>
      <h1 className="not-found-title">{t('notFound.title')}</h1>
      <p className="not-found-description">{t('notFound.description')}</p>
      <div className="not-found-actions">
        <Link to={PURCHASE_MARKETING_ROUTES.HOME} className="btn-primary">
          {t('notFound.homeLink')}
        </Link>
        <Link to={PURCHASE_MARKETING_ROUTES.CONTACT} className="btn-secondary">
          {t('notFound.contactLink')}
        </Link>
      </div>
    </div>
  );
}

export default NotFoundPage;
